// Artifacts tab: the Features/PRs/Tickets sub-nav, the PR + ticket tables, and the
// feature manager (hierarchical list with ship-toggle, nest-under, move-to-top,
// delete). Feature mutations POST to /api/features* and reload.
import { state, $, esc, usd, dayOf, get, post, complexityLabel } from './core'
var CX_OPTS = [['', 'Not tagged'], ['1', 'Trivial'], ['2', 'Simple'], ['3', 'Moderate'], ['4', 'Complex'], ['5', 'Highly Complex']];
import { syncHash } from './router'
import { filterByArtifact, filterByArtifactId, setView } from './sessions'
import {
  ticketStatusBadge, ticketTitleText, ticketTitleIsPlaceholder, ticketCostText,
  ticketPointsText, ticketMatches, ticketTree, ticketFilterKeepingAncestors,
  ticketFilterMeasured, issueTypeLabel, ticketDescendantCount, defaultCollapsed,
  visibleTicketEntries, TICKET_COLS,
} from './ticket-table'

// The complexity tag as a pill — reuses the grey b-null badge (same as the other
// artifacts-view pills). Empty for untagged features / PRs with no recorded churn.
// PRs get the raw line count as a tooltip since their bucket is derived from diff size.
function complexityPill(kind, complexity) {
  var label = complexityLabel(kind, complexity);
  if (!label) return '';
  var title = kind === 'pr' ? ' title="' + esc(Number(complexity) + ' lines changed') + '"' : '';
  return '<span class="badge b-null"' + title + '>' + esc(label) + '</span>';
}

export function renderArtKindSeg() {
  var opts = [['feature', 'Features'], ['pr', 'PRs'], ['ticket', 'Tickets']];
  $('#artKindSeg').innerHTML = opts.map(function (o) {
    return '<button class="' + (o[0] === state.artKind ? 'on' : '') + '" data-k="' + o[0] + '">' + o[1] + '</button>';
  }).join('');
  Array.prototype.forEach.call($('#artKindSeg').children, function (btn) {
    btn.onclick = function () { setArtKind(btn.getAttribute('data-k')); };
  });
}

// Switch the Artifacts sub-tab (Features | PRs) from the segment buttons, and
// mirror it into the URL. Switching kind resets the table's search/sort (each kind
// gets a fresh list). No-op when already on that kind.
export function setArtKind(kind) {
  state.view = 'artifacts';
  if (state.artKind === kind) { syncHash(); return; }
  state.artKind = kind;
  state.art = { q: '', sort: 'created', dir: 'desc' };
  renderArtKindSeg();
  loadArtifacts();
  syncHash();
}

// ---- URL <-> artifacts-list bridge (used by the router) ----------------------

// The current artifacts-table state as URL query params: free-text search (q) and,
// for the sortable tables (PRs, tickets), the column sort. Defaults (created/desc)
// are omitted. The feature manager has no column sort, so it contributes only `q`.
export function getArtifactParams(): Record<string, string> {
  var q: Record<string, string> = {};
  if (state.art.q) q.q = state.art.q;
  if (state.artKind === 'pr' || state.artKind === 'ticket') {
    if (state.art.sort && state.art.sort !== 'created') q.sort = state.art.sort;
    if (state.art.dir === 'asc') q.dir = 'asc';
  }
  return q;
}

// Restore the artifacts kind + table search/sort from the URL, then reload. Used
// by the router on Back/Forward and deep links (inverse of getArtifactParams).
export function applyArtifactParams(kind, query) {
  state.view = 'artifacts';
  state.artKind = kind === 'pr' || kind === 'feature' || kind === 'ticket' ? kind : 'feature';
  state.art = {
    q: query.q || '',
    sort: query.sort || 'created',
    dir: query.dir === 'asc' ? 'asc' : 'desc',
  };
  renderArtKindSeg();
  loadArtifacts();
}

function renderArtifacts(rows, kind) {
  if (kind === 'feature') { renderFeatureManager(rows || []); return; }
  if (kind === 'ticket') { renderTicketTab(rows || []); return; }
  renderPrTab(rows || []);
}

// The PR sub-tab: a search box (by title or identifier) over a sortable table.
// Rows are held in module state so search + column sort re-render only the table
// body, leaving the (outside) search input focused.
var prRows = [];
// Column sort lives in state.art (state.art.sort / .dir) so it round-trips through
// the URL; the search box value lives in state.art.q.
// [key, label, numeric?] — numeric columns right-align and default to descending.
var PR_COLS = [['pr', 'Pull request', 0], ['status', 'Status', 0], ['sessions', 'Sessions', 1],
  ['cost', 'Cost', 1], ['created', 'Created', 1], ['merged', 'Merged', 1]];

function renderPrTab(rows) {
  prRows = rows;
  if (!rows.length) {
    $('#artifacts').innerHTML = '<div class="empty">No PRs linked yet. A PR appears here once it\'s linked to a session — from the transcript (gh pr create / merge) or by matching code you pushed yourself.</div>';
    return;
  }
  $('#artifacts').innerHTML =
    '<input id="pr-search" class="feat-search" type="search" placeholder="Search PRs by title or #identifier…" autocomplete="off" />' +
    '<div id="pr-table-wrap"></div>';
  var srch = $('#pr-search');
  srch.value = state.art.q || ''; // restore a URL-driven search
  srch.oninput = function () { state.art.q = srch.value; syncHash({ replace: true }); renderPrTable(); };
  renderPrTable();
}

// Sort key for a column; `created` falls back to merge time (mirrors the server COALESCE).
function prVal(r, col) {
  if (col === 'pr') return Number(r.ident) || 0;
  if (col === 'status') return (r.status || '').toLowerCase();
  if (col === 'sessions') return r.sessions || 0;
  if (col === 'cost') return r.costUsd || 0;
  if (col === 'created') return r.createdAt || r.completedAt || '';
  if (col === 'merged') return r.completedAt || '';
  return '';
}

function renderPrTable() {
  var search = $('#pr-search');
  var q = (search && search.value || '').trim().toLowerCase();
  var rows = prRows.filter(function (r) {
    if (!q) return true;
    var hay = ((r.title || '') + ' #' + (r.ident || '') + ' ' + (r.repo || '') + ' ' + (r.externalId || '')).toLowerCase();
    return hay.indexOf(q) !== -1;
  });
  var dir = state.art.dir === 'asc' ? 1 : -1;
  rows.sort(function (a, b) {
    var x = prVal(a, state.art.sort), y = prVal(b, state.art.sort);
    return x < y ? -dir : x > y ? dir : 0;
  });

  var head = '<tr>' + PR_COLS.map(function (c) {
    var arrow = c[0] === state.art.sort ? (state.art.dir === 'asc' ? ' &#9652;' : ' &#9662;') : '';
    return '<th class="pr-th' + (c[2] ? ' num' : '') + '" data-sort="' + c[0] + '">' + c[1] + arrow + '</th>';
  }).join('') + '<th></th></tr>';
  var body = rows.map(function (r) {
    var key = r.externalId || r.ident;
    var idLabel = (r.repo ? esc(r.repo) + ' ' : '') + '#' + esc(r.ident);
    var idHtml = r.externalId
      ? '<a class="pr-link" href="' + esc(r.externalId) + '" target="_blank" rel="noopener">' + idLabel + '</a>'
      : idLabel;
    // Inline AI-attribution tag beside the PR id (alternative to the AI-written column).
    var aiTag = r.aiPct != null ? ' <span class="pr-ai-tag" title="Share of this PR written by AI across all sessions">' + Math.round(r.aiPct * 100) + '% AI</span>' : '';
    var titleHtml = r.title ? '<div class="pr-title">' + esc(r.title) + '</div>' : '';
    return '<tr class="arow" data-art="' + esc(key) + '" data-kind="pr">' +
      '<td>' + idHtml + ' ' + complexityPill('pr', r.complexity) + aiTag + titleHtml + '</td>' +
      '<td>' + (r.status ? esc(r.status) : '—') + '</td>' +
      '<td class="num">' + r.sessions + '</td>' +
      '<td class="num">' + usd(r.costUsd) + '</td>' +
      '<td class="num">' + (esc(dayOf(r.createdAt)) || '—') + '</td>' +
      '<td class="num">' + (esc(dayOf(r.completedAt)) || '—') + '</td>' +
      '<td><button class="btn sess-btn" data-art="' + esc(key) + '" data-id="' + esc(r.id) + '">Sessions &rarr;</button></td></tr>';
  }).join('');
  var note = rows.length ? '' : '<div class="empty">No PRs match your search.</div>';
  $('#pr-table-wrap').innerHTML = '<table>' + head + body + '</table>' + note;

  Array.prototype.forEach.call(document.querySelectorAll('#pr-table-wrap .pr-th'), function (th) {
    th.onclick = function () {
      var col = th.getAttribute('data-sort');
      if (state.art.sort === col) state.art.dir = state.art.dir === 'asc' ? 'desc' : 'asc';
      else { state.art.sort = col; state.art.dir = (col === 'pr' || col === 'status') ? 'asc' : 'desc'; }
      syncHash({ replace: true });
      renderPrTable();
    };
  });
  Array.prototype.forEach.call(document.querySelectorAll('#pr-table-wrap .sess-btn'), function (btn) {
    // By exact id, not the PR number as text: `#1` as a substring also matches #10,
    // #101, #101… — the same identity bug the ticket drill-in had. A PR always has
    // block rows, so this reads "which sessions did work on this PR".
    btn.onclick = function () { filterByArtifactId(btn.getAttribute('data-id'), btn.getAttribute('data-art'), false, 'pr'); };
  });
}

// The Tickets sub-tab (AL-126). Like the PR table — same search box, same
// click-to-sort header, same "Sessions →" drill-in — but HIERARCHICAL: Jira's
// epic → story → subtask nesting is the shape users think in, so the table shows
// it rather than 93 flat keys. Epics are ordinary rows of this table — every Jira
// issue is `kind='ticket'` (design §3b), so nothing special is needed to include
// them, and without them the stories under each phase would read as parentless.
//
// The column sort orders SIBLINGS, never the whole flat list — see ticketTree.
// Sorting a tree flat would silently reparent every row.
//
// No resolved-by column: its only data source was Phase 3 (AL-109), which is
// Won't do, so `artifact_links` is empty and the column would be blank on every
// row. See the design doc §5.1.
var ticketRows = [];
// TICKET_COLS moved to ticket-table.ts so a unit test can pin the column ORDER against
// the hand-written body row below — see the note there.
//
// Points is SHOWN again. It was hidden while the connector imported none (the REST
// gateway never asked Jira for the field, so the cell read '—' on all 111 rows and the
// column was dead width); with the field on the wire the AL project is fully estimated,
// and the 26 rows that legitimately have no estimate — the AG-* project, plus
// false-positive keys like AL-34 that no Jira issue backs — still read '—'.
// Fixed widths for every column BUT the ticket one, which takes the remainder — see
// the colgroup note in renderTicketTable. Each is sized to its widest real content
// ('not measured', 'In progress', an ISO day) so nothing wraps to a second line.
// Each is sized to the WIDER of its header ('SESSIONS' uppercase + letter-spacing)
// and its widest real value ('not measured', 'In progress', an ISO day) — a fixed
// layout clips rather than growing, so an under-measured column truncates silently.
// `points` is sized by its HEADER ('POINTS' uppercase), not its values: estimates are
// one or two digits, so the word is always the wider of the two.
var TICKET_COL_W = { status: '108px', sessions: '96px', cost: '116px', points: '84px', created: '112px' };

// Which rows the user has folded, by artifact id. Seeded from `defaultCollapsed`
// on each load (see TICKET_EXPAND_THROUGH) and then owned by the user, so a
// re-render from a sort or a search keeps their folds — only switching sub-tab or
// reloading resets it. Not in `state`/the URL: it's transient view furniture, and
// 93 ids would swamp a shareable link.
var ticketCollapsed: Record<string, boolean> | null = null;
// Roots and their direct children are open; depth 2+ (a story's subtasks) is folded.
// The phase structure is the useful overview — subtasks are detail you ask for.
var TICKET_EXPAND_THROUGH = 1;
// Whether the sighted-but-never-charged rows are shown. Off by default: they are
// most of the table and carry no spend, so the default answers "where did the money
// go" and the toggle answers "what else was in our orbit". Transient view furniture
// like `ticketCollapsed` — not in `state`/the URL.
var ticketShowUnmeasured = false;

function renderTicketTab(rows) {
  ticketRows = rows;
  ticketCollapsed = null; // re-seeded on the first render below, once the tree exists
  ticketShowUnmeasured = false;
  if (!rows.length) {
    $('#artifacts').innerHTML = '<div class="empty">No tickets linked yet. A ticket appears here once a session names its key — in a Jira MCP call, an <code>acli</code> command, a branch name or a commit message. Configure the Jira connector in Settings to give them titles, statuses and story points.</div>';
    return;
  }
  $('#artifacts').innerHTML =
    '<input id="ticket-search" class="feat-search" type="search" placeholder="Search tickets by key or title…" autocomplete="off" />' +
    '<div id="ticket-table-wrap"></div>';
  var srch = $('#ticket-search');
  srch.value = state.art.q || ''; // restore a URL-driven search
  srch.oninput = function () { state.art.q = srch.value; syncHash({ replace: true }); renderTicketTable(); };
  renderTicketTable();
}

function renderTicketTable() {
  var search = $('#ticket-search');
  var q = (search && search.value) || '';
  // Unmeasured rows leave first, so the search (and the ancestor pull below) runs
  // over the population the reader has chosen to look at — a search must not
  // resurrect rows the toggle hides, or the toggle reads as broken.
  var split = ticketFilterMeasured(ticketRows);
  var pool = ticketShowUnmeasured ? ticketRows : split.kept;
  // Ancestors of a match come along, so a hit on a subtask renders under its epic
  // instead of floating at depth 0.
  var kept = ticketFilterKeepingAncestors(pool, q);
  var entries = ticketTree(kept, state.art.sort, state.art.dir === 'asc' ? 'asc' : 'desc');
  // Subtree sizes off the FULL tree, before folding: a collapsed row's "+N" must
  // count what it hides, and the cost tooltip's wording asks about real children,
  // not visible ones.
  var kids = ticketDescendantCount(entries);
  if (!ticketCollapsed) ticketCollapsed = defaultCollapsed(entries, TICKET_EXPAND_THROUGH);
  // A search reaches past the fold: a user who typed a subtask's key means to see
  // it, and leaving it hidden behind a collapsed story would read as "no match".
  // Their folds are remembered, not discarded — clearing the box restores them.
  var visible = q.trim() ? entries : visibleTicketEntries(entries, ticketCollapsed);

  // A fixed layout with declared widths, unlike the PR table's auto one. The ticket
  // cell is a single line (key + title + pill), so its intrinsic width is the whole
  // title — under `auto` that column claims everything, squeezing the numerics into
  // wrapped two-line cells and pushing the action column past the card's edge. Fixed
  // widths also stop the columns shifting as a search changes the longest title.
  var cols = '<colgroup>' + TICKET_COLS.map(function (c) {
    return '<col' + (TICKET_COL_W[c[0]] ? ' style="width:' + TICKET_COL_W[c[0]] + '"' : '') + ' />';
  }).join('') + '<col style="width:112px" /></colgroup>';
  var head = '<tr>' + TICKET_COLS.map(function (c) {
    var arrow = c[0] === state.art.sort ? (state.art.dir === 'asc' ? ' &#9652;' : ' &#9662;') : '';
    return '<th class="pr-th' + (c[2] ? ' num' : '') + '" data-sort="' + c[0] + '">' + c[1] + arrow + '</th>';
  }).join('') + '<th></th></tr>';

  var body = visible.map(function (e) {
    var r = e.node;
    // Drill-in and row identity use the KEY (AL-126), not the id (jira:AL-126) —
    // the sessions artifact filter matches on ident/title/external_id.
    var key = r.ident || r.id;
    // Link to the Jira issue only when the connector filled external_id; before
    // that there is no site URL to build one from, so the key renders as plain text.
    var keyHtml = r.externalId
      ? '<a class="pr-link tkt-key" href="' + esc(r.externalId) + '" target="_blank" rel="noopener">' + esc(key) + '</a>'
      : '<span class="tkt-key">' + esc(key) + '</span>';
    // Indent marks depth, matching the feature manager's idiom (22px/level). The
    // elbow the feature manager uses is gone: the toggle occupies that slot, and two
    // glyphs before the key made the column noisy.
    var indent = e.depth * 22;
    var n = kids[r.id] || 0;
    var isCollapsed = !!ticketCollapsed[r.id];
    // A leaf still reserves the toggle's width, so keys stay left-aligned down a
    // sibling run whether or not each one happens to have children.
    var toggle = n
      ? '<button class="tkt-fold" type="button" data-fold="' + esc(r.id) + '" aria-expanded="' + (isCollapsed ? 'false' : 'true') +
        '" title="' + (isCollapsed ? 'Expand' : 'Collapse') + '">' + (isCollapsed ? '&#9656;' : '&#9662;') + '</button>'
      : '<span class="tkt-fold-sp"></span>';
    // The type pill says WHY a row sits at its depth, in the site's own wording
    // ('Epic', 'Workstream', 'Sub-task') rather than a normalized guess.
    var type = issueTypeLabel(r);
    var typePill = type ? '<span class="badge b-null tkt-type">' + esc(type) + '</span>' : '';
    // Key and title on ONE line ("AL-108 Phase 2: …"): the key alone doesn't say what
    // a ticket is, and a reader scanning the tree shouldn't have to drop to a second
    // line for the only human-readable field. Still two elements, so the placeholder
    // ("No title — not fetched from Jira") keeps its muted-italic treatment.
    var titleHtml = '<span class="tkt-name' + (ticketTitleIsPlaceholder(r) ? ' unset' : '') + '">' + esc(ticketTitleText(r)) + '</span>';
    // Only on a collapsed row: how much is hidden. Without it, folding a phase looks
    // like its subtasks vanished from the list.
    var hidden = isCollapsed ? '<span class="tkt-hidden" title="' + n + ' nested ' + (n === 1 ? 'ticket' : 'tickets') +
      ' hidden">+' + n + '</span>' : '';
    var st = ticketStatusBadge(r.status);
    // The cost cell carries a tooltip when unmeasured, because "not measured" is the
    // kind of answer that invites "…why not?". Both cost and sessions now roll the
    // SUBTREE up, so an unmeasured parent is no longer the common "spend sits in my
    // subtasks" case — it means nothing anywhere beneath it was attributed either.
    var unmeasuredTip = n
      ? 'No session block was attributed to this row or to anything nested under it, so its spend was never measured. This is not $0.'
      : 'No block of any session was attributed to this ticket, so its spend was never measured. This is not $0.';
    var costCell = r.costMeasured
      ? esc(ticketCostText(r))
      : '<span class="unset" title="' + esc(unmeasuredTip) + '">' + esc(ticketCostText(r)) + '</span>';
    // Points read as an em-dash when nobody estimated, muted and with the reason on
    // hover — the same treatment as an unmeasured cost, because it is the same class of
    // fact: an absence, not a zero. A team that estimates stories but not subtasks is
    // normal, so this cell is blank on real rows and must not look like a bug.
    var pointsCell = r.storyPoints == null
      ? '<span class="unset" title="No story-point estimate on this issue in Jira. This is not 0 points.">' +
        esc(ticketPointsText(r)) + '</span>'
      : esc(ticketPointsText(r));
    return '<tr class="arow" data-art="' + esc(key) + '" data-kind="' + esc(r.kind === 'feature' ? 'feature' : 'ticket') + '">' +
      '<td><div class="tkt-cell" style="padding-left:' + indent + 'px">' + toggle + keyHtml + titleHtml + typePill + hidden + '</div></td>' +
      '<td><span class="badge ' + st.cls + '">' + esc(st.label) + '</span></td>' +
      '<td class="num">' + r.sessions + '</td>' +
      '<td class="num">' + costCell + '</td>' +
      // Points. This row is hand-written rather than mapped over TICKET_COLS, so the
      // cell order here has to track that list by hand or the columns shear by one.
      '<td class="num">' + pointsCell + '</td>' +
      '<td class="num">' + (esc(dayOf(r.createdAt)) || '—') + '</td>' +
      '<td><button class="btn sess-btn" data-art="' + esc(key) + '" data-id="' + esc(r.id) +
      // ALWAYS the subtree on this tab, so the button matches the Sessions and Cost
      // cells beside it — both roll the subtree up. Unconditional rather than gated
      // on `kids`: a leaf's subtree is only itself, so the flag would change nothing
      // there, and `kids` counts the SEARCHED tree — a search that hid a parent's
      // children would otherwise quietly shrink its drill-in.
      '" data-subtree="1">Sessions &rarr;</button></td></tr>';
  }).join('');

  // The counted way back to the hidden population. A bare "show more" would leave
  // "more of what?" unanswered; the tooltip carries the same not-$0 explanation as
  // the cost cell, because this is the same absence at table grain.
  var toggle = split.hidden
    ? '<label class="tkt-unmeasured" title="Tickets sessions mentioned (a branch name, commit, PR title or Jira call) but never had a session block attributed to — listed without spend. Hidden by default so the table answers where the money went.">' +
      '<input type="checkbox" id="ticket-show-unmeasured"' + (ticketShowUnmeasured ? ' checked' : '') + ' /> ' +
      'Show ' + split.hidden + ' unmeasured ticket' + (split.hidden === 1 ? '' : 's') + '</label>'
    : '';
  var note = entries.length
    ? ''
    : q.trim()
      ? '<div class="empty">No tickets match your search.</div>'
      // Every ticket is unmeasured and the toggle is off — say so, or the tab
      // reads as "no tickets" while the sub-tab badge says otherwise.
      : '<div class="empty">No measured tickets yet — no session block has been attributed to any ticket. Tick the box above to see the ' + split.hidden + ' unmeasured one' + (split.hidden === 1 ? '' : 's') + '.</div>';
  $('#ticket-table-wrap').innerHTML = toggle + '<table class="tkt-table">' + cols + head + body + '</table>' + note;

  var showUnmeasured = document.getElementById('ticket-show-unmeasured') as HTMLInputElement | null;
  if (showUnmeasured) {
    showUnmeasured.onchange = function () {
      ticketShowUnmeasured = showUnmeasured!.checked;
      renderTicketTable();
    };
  }

  Array.prototype.forEach.call(document.querySelectorAll('#ticket-table-wrap .pr-th'), function (th) {
    th.onclick = function () {
      var col = th.getAttribute('data-sort');
      if (state.art.sort === col) state.art.dir = state.art.dir === 'asc' ? 'desc' : 'asc';
      // Text columns open ascending (A→Z reads naturally); numeric ones open
      // descending (the biggest number is the interesting one). Same rule as PRs.
      else { state.art.dir = (col === 'key' || col === 'status') ? 'asc' : 'desc'; state.art.sort = col; }
      syncHash({ replace: true });
      renderTicketTable();
    };
  });
  Array.prototype.forEach.call(document.querySelectorAll('#ticket-table-wrap .tkt-fold'), function (btn) {
    btn.onclick = function (ev) {
      // The row is clickable (`.arow` drills in) — without this a fold would also
      // navigate away from the table it just re-folded.
      ev.stopPropagation();
      var id = btn.getAttribute('data-fold');
      ticketCollapsed[id] = !ticketCollapsed[id];
      renderTicketTable();
    };
  });
  Array.prototype.forEach.call(document.querySelectorAll('#ticket-table-wrap .sess-btn'), function (btn) {
    // By exact id, which removed the need to pass a kind at all (back when an epic was
    // stored as `kind='feature'`, a kind-filtered text drill-in had to special-case
    // it). A key was a bad identity anyway: `AL-1` as a substring matches AL-10 … AL-149.
    //
    // The kind is left to filterByArtifactId's id-only default ('ticket') rather than
    // read off the button, which never carried a data-kind: the attribute resolved to
    // '' on every row, and '' focused the drawer's transcript on nothing, so every
    // ticket drill-in landed at turn 1. Every row in this table is a ticket now, epics
    // included, so that default is simply the right answer for all of them.
    btn.onclick = function () {
      filterByArtifactId(btn.getAttribute('data-id'), btn.getAttribute('data-art'), btn.getAttribute('data-subtree') === '1');
    };
  });
}

function renderFeatureManager(rows) {
  var html = '';
  if (!rows.length) {
    html += '<div class="empty">No features yet. Add one below, or enrich sessions to propose features.</div>';
  } else {
    html += '<input id="feat-search" class="feat-search" type="search" placeholder="Search features…" autocomplete="off" />';
    html += '<div class="feat-list">' +
      '<div class="feat-head"><span>Feature</span><span>Repos</span><span>Last session</span><span class="fh-num">Sessions</span><span class="fh-num">Cost</span><span></span></div>';
    flattenFeatures(rows).forEach(function (e) {
      var r = e.node, indent = e.depth * 22;
      var twig = e.depth ? '<span class="feat-twig">&#8627;</span> ' : '';
      var shipped = !!r.completedAt;
      // "open" badge omitted — the top-level "Mark shipped" button implies open status.
      var statusHtml = shipped
        ? '<span class="badge b-success">shipped ' + esc(dayOf(r.completedAt)) + '</span>'
        : '';
      var proposed = r.source === 'derived' ? '<span class="tag">proposed</span>' : '';
      var repos = (r.repos && r.repos.length) ? r.repos.join(', ') : '—';
      var last = r.lastSessionAt ? dayOf(r.lastSessionAt) : '—';
      // Secondary actions (nest-under · complexity · move-to-top · delete) collapse
      // into a per-row hamburger; only the indent shifts, so columns stay aligned.
      var nest = '<select class="feat-nest" data-id="' + esc(r.id) + '">' +
        nestUnderOptions(rows, r.id, descendantsOf(rows, r.id)) + '</select>';
      var cxVal = r.complexity ? String(r.complexity) : '';
      var cxSelect = '<select class="feat-cx" data-id="' + esc(r.id) + '">' +
        CX_OPTS.map(function (o) { return '<option value="' + o[0] + '"' + (o[0] === cxVal ? ' selected' : '') + '>' + o[1] + '</option>'; }).join('') +
        '</select>';
      var toTop = r.parentId
        ? '<button class="menu-item" data-act="totop" data-id="' + esc(r.id) + '">Move to top level</button>'
        : '';
      html += '<div class="feat-row" data-name="' + esc((r.title || '').toLowerCase()) +
        '" data-id="' + esc(r.id) + '" data-parent="' + esc(r.parentId || '') + '">' +
        '<div class="feat-name" style="padding-left:' + indent + 'px">' + twig +
          '<span class="nm" title="' + esc(r.title || '') + '">' +
          esc(r.title || '(untitled)') + '</span> ' + proposed + ' ' + statusHtml + ' ' + complexityPill('feature', r.complexity) + '</div>' +
        '<span class="feat-repos" title="' + esc(repos) + '">' + esc(repos) + '</span>' +
        '<span class="feat-last" title="' + esc(r.lastSessionAt || '') + '">' + esc(last) + '</span>' +
        '<span class="feat-num">' + r.sessions + '</span>' +
        '<span class="feat-num">' + usd(r.costUsd) + '</span>' +
        '<div class="feat-actions">' +
          '<button class="btn ship-btn' + (shipped ? ' shipped' : '') + '" data-act="toggle" data-id="' + esc(r.id) + '" data-completed="' + (shipped ? '1' : '0') + '">' +
            (shipped ? 'Reopen' : 'Mark shipped') + '</button>' +
          // By id, and over the subtree for a parent feature: matching on the TITLE
          // as a substring made "Auth" pull in every session touching "Auth tokens"
          // and "OAuth", and an untitled feature matched everything.
          '<button class="btn sess-btn" data-art="' + esc(r.title || '') + '" data-id="' + esc(r.id) +
          // descendantsOf returns a SET (id -> true), so count its keys.
          '" data-subtree="' + (Object.keys(descendantsOf(rows, r.id)).length ? '1' : '') + '">Sessions &rarr;</button>' +
          '<div class="feat-menu-wrap">' +
            '<button class="feat-menu-btn" aria-label="More actions">&#8943;</button>' +
            '<div class="feat-menu">' +
              '<div class="menu-nest"><label>Nest under</label>' + nest + '</div>' +
              '<div class="menu-nest"><label>Complexity</label>' + cxSelect + '</div>' +
              toTop +
              '<div class="menu-sep"></div>' +
              '<button class="menu-item danger" data-act="delete" data-id="' + esc(r.id) + '" data-title="' + esc(r.title || '') + '">Delete</button>' +
            '</div>' +
          '</div>' +
        '</div></div>';
    });
    html += '</div>';
  }
  // New-feature form lives at the bottom, collapsed behind an "Add feature" button
  // to keep the list uncluttered.
  html += '<div class="feat-add">' +
    '<button class="btn" id="nf-toggle">+ Add feature</button>' +
    '<div class="feat-new" id="nf-form" hidden>' +
      '<input id="nf-title" placeholder="New feature title" />' +
      '<select id="nf-parent">' + featureParentOptions(rows, '', '', null) + '</select>' +
      '<select id="nf-complexity"><option value="">Complexity (optional)</option>' +
        CX_OPTS.slice(1).map(function (o) { return '<option value="' + o[0] + '">' + o[1] + '</option>'; }).join('') + '</select>' +
      '<button class="btn" id="nf-add">Add</button>' +
      '<button class="btn" id="nf-cancel">Cancel</button>' +
    '</div></div>';
  $('#artifacts').innerHTML = html;
  wireFeatureManager();
}

// Options for the per-row "Nest under…" action — candidate parents only (no
// "(top level)" option; that is its own explicit button). Excludes self + any
// descendants (would create a cycle).
function nestUnderOptions(rows, id, excludeSet) {
  var opts = '<option value="" disabled selected>Nest under…</option>';
  rows.forEach(function (r) {
    if (r.id === id) return;
    if (excludeSet && excludeSet[r.id]) return;
    opts += '<option value="' + esc(r.id) + '">' + esc(r.title || r.id) + '</option>';
  });
  return opts;
}

function featureParentOptions(rows, selectedId, excludeId, excludeSet) {
  var opts = '<option value=""' + (selectedId ? '' : ' selected') + '>(top level)</option>';
  rows.forEach(function (r) {
    if (r.id === excludeId) return;
    if (excludeSet && excludeSet[r.id]) return;
    opts += '<option value="' + esc(r.id) + '"' + (r.id === selectedId ? ' selected' : '') + '>' + esc(r.title || r.id) + '</option>';
  });
  return opts;
}

function descendantsOf(rows, id) {
  var children = {};
  rows.forEach(function (r) { var p = r.parentId || ''; (children[p] = children[p] || []).push(r.id); });
  var out = {}, stack = (children[id] || []).slice();
  while (stack.length) { var x = stack.pop(); if (out[x]) continue; out[x] = true; (children[x] || []).forEach(function (c) { stack.push(c); }); }
  return out;
}

function flattenFeatures(rows) {
  // Most-recent-activity first: order roots and each sibling group by last session
  // time descending (undated sorts last). Hierarchy is preserved — a parent still
  // renders before its children; only the order among siblings changes.
  function recency(a, b) { return String(b.lastSessionAt || '').localeCompare(String(a.lastSessionAt || '')); }
  var byId = {}; rows.forEach(function (r) { byId[r.id] = r; });
  var children = {};
  rows.forEach(function (r) {
    var p = r.parentId && byId[r.parentId] ? r.parentId : '';
    (children[p] = children[p] || []).push(r);
  });
  Object.keys(children).forEach(function (k) { children[k].sort(recency); });
  var out = [], visited = {};
  (function walk(key, depth) {
    (children[key] || []).forEach(function (r) {
      if (visited[r.id]) return; visited[r.id] = true;
      out.push({ node: r, depth: depth });
      walk(r.id, depth + 1);
    });
  })('', 0);
  rows.slice().sort(recency).forEach(function (r) { if (!visited[r.id]) { visited[r.id] = true; out.push({ node: r, depth: 0 }); } });
  return out;
}

function wireFeatureManager() {
  function each(sel, fn) { Array.prototype.forEach.call(document.querySelectorAll(sel), fn); }

  // Add-feature: a collapsed button that expands into the full form on click.
  var nfToggle = $('#nf-toggle'), nfForm = $('#nf-form'), nfTitle = $('#nf-title');
  if (nfToggle) nfToggle.onclick = function () {
    nfForm.hidden = false; nfToggle.hidden = true;
    if (nfTitle) nfTitle.focus();
  };
  var nfCancel = $('#nf-cancel');
  if (nfCancel) nfCancel.onclick = function () {
    nfForm.hidden = true; nfToggle.hidden = false;
    if (nfTitle) nfTitle.value = '';
  };
  var add = $('#nf-add');
  if (add) add.onclick = function () {
    var title = nfTitle.value.trim();
    if (!title) return;
    var cx = $('#nf-complexity').value || undefined;
    post('/api/features', { title: title, parentId: $('#nf-parent').value || undefined, complexity: cx ? Number(cx) : undefined }).then(loadArtifacts);
  };
  if (nfTitle) nfTitle.onkeydown = function (ev) { if (ev.key === 'Enter' && add) add.onclick(); };

  // Hamburger menus: fixed-positioned (so the scroll container's overflow can't
  // clip them) and only one open at a time. Clicks inside a menu don't bubble to
  // the document closer, keeping the nest <select> usable.
  each('#artifacts .feat-menu-btn', function (btn) {
    btn.onclick = function (ev) {
      ev.stopPropagation();
      var menu = btn.parentNode.querySelector('.feat-menu');
      var wasOpen = menu.classList.contains('on');
      closeFeatMenus();
      if (wasOpen) return;
      menu.classList.add('on');
      var rect = btn.getBoundingClientRect();
      menu.style.top = (rect.bottom + 4) + 'px';
      menu.style.left = (rect.right - menu.offsetWidth) + 'px';
    };
  });
  each('#artifacts .feat-menu', function (m) { m.onclick = function (ev) { ev.stopPropagation(); }; });
  var list = $('#artifacts .feat-list');
  if (list) list.onscroll = closeFeatMenus; // fixed menus don't track the scrolled button

  each('#artifacts .sess-btn', function (b) {
    b.onclick = function () {
      filterByArtifactId(b.getAttribute('data-id'), b.getAttribute('data-art'), b.getAttribute('data-subtree') === '1', 'feature');
    };
  });
  each('#artifacts [data-act="toggle"]', function (b) {
    b.onclick = function () {
      post('/api/features/update', { id: b.getAttribute('data-id'), completed: b.getAttribute('data-completed') !== '1' }).then(loadArtifacts);
    };
  });
  each('#artifacts [data-act="delete"]', function (b) {
    b.onclick = function () {
      if (!window.confirm('Delete feature "' + (b.getAttribute('data-title') || '') + '"? Any sub-features move up to its parent.')) return;
      post('/api/features/delete', { id: b.getAttribute('data-id') }).then(loadArtifacts);
    };
  });
  each('#artifacts .feat-nest', function (sel) {
    sel.onchange = function () {
      if (!sel.value) return;
      post('/api/features/update', { id: sel.getAttribute('data-id'), parentId: sel.value }).then(loadArtifacts);
    };
  });
  each('#artifacts .feat-cx', function (sel) {
    sel.onchange = function () {
      var val = sel.value ? Number(sel.value) : null;
      post('/api/features/update', { id: sel.getAttribute('data-id'), complexity: val }).then(loadArtifacts);
    };
  });
  each('#artifacts [data-act="totop"]', function (b) {
    b.onclick = function () {
      post('/api/features/update', { id: b.getAttribute('data-id'), parentId: null }).then(loadArtifacts);
    };
  });

  // Local name filter. A match reveals its WHOLE subtree (so searching an epic
  // shows everything under it) plus its ancestors (so the match keeps its place
  // in the tree). Pure show/hide in place — no refetch, keeps input focus.
  function filterFeatRows(qRaw) {
    var q = (qRaw || '').trim().toLowerCase();
    var rows = Array.prototype.slice.call(document.querySelectorAll('#artifacts .feat-row'));
    if (!q) { rows.forEach(function (r) { r.style.display = ''; }); return; }
    var childrenOf = {}, parentOf = {};
    rows.forEach(function (r) {
      var id = r.getAttribute('data-id'), p = r.getAttribute('data-parent') || '';
      parentOf[id] = p;
      (childrenOf[p] = childrenOf[p] || []).push(id);
    });
    var visible = {};
    function revealSubtree(id) {
      if (visible[id]) return;
      visible[id] = true;
      (childrenOf[id] || []).forEach(revealSubtree);
    }
    function revealAncestors(id) {
      var p = parentOf[id];
      while (p) { visible[p] = true; p = parentOf[p]; }
    }
    rows.forEach(function (r) {
      if ((r.getAttribute('data-name') || '').indexOf(q) !== -1) {
        var id = r.getAttribute('data-id');
        revealSubtree(id);
        revealAncestors(id);
      }
    });
    rows.forEach(function (r) { r.style.display = visible[r.getAttribute('data-id')] ? '' : 'none'; });
  }
  var search = $('#feat-search');
  if (search) {
    search.value = state.art.q || ''; // restore a URL-driven search
    search.oninput = function () { state.art.q = search.value; syncHash({ replace: true }); filterFeatRows(search.value); };
    if (search.value) filterFeatRows(search.value);
  }

  // One-time global closers: outside click and Escape dismiss any open menu.
  // Idempotent across reloads — they query the live DOM each time.
  if (!featMenusWired) {
    featMenusWired = true;
    document.addEventListener('click', closeFeatMenus);
    document.addEventListener('keydown', function (ev) { if (ev.key === 'Escape') closeFeatMenus(); });
  }
}

// Module-level so the global listeners are attached only once. closeFeatMenus is
// hoisted out of wireFeatureManager so those one-time listeners don't capture a
// stale closure from the first render.
var featMenusWired = false;
function closeFeatMenus() {
  Array.prototype.forEach.call(document.querySelectorAll('#artifacts .feat-menu.on'), function (m) { m.classList.remove('on'); });
}

// When a deep-link (Explore Q1) lands here, pre-fill that sub-tab's search so the
// list narrows to the one artifact instead of dropping the user into a full list.
var pendingArtSearch = '';
var ART_SEARCH_INPUT = { pr: 'pr-search', ticket: 'ticket-search', feature: 'feat-search' };
function applyArtSearch() {
  if (!pendingArtSearch) return;
  var input = document.getElementById(ART_SEARCH_INPUT[state.artKind] || 'feat-search') as any;
  if (input) { input.value = pendingArtSearch; if (input.oninput) input.oninput(); }
  pendingArtSearch = '';
}

export function loadArtifacts() {
  get('/api/artifacts?kind=' + encodeURIComponent(state.artKind)).then(function (rows) {
    renderArtifacts(rows, state.artKind);
    applyArtSearch();
  });
}

// Open the Artifacts tab at a sub-kind (feature/pr) with its search pre-filled to
// `query` — the list ends up narrowed to that artifact (showing its cost).
export function openArtifactSearch(kind, query) {
  setView('artifacts');
  state.artKind = kind;
  pendingArtSearch = query || '';
  renderArtKindSeg();
  loadArtifacts();
}
